import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { 
  FileText, Edit, Save, RefreshCw, Plus, Trash2, 
  ChevronDown, ChevronUp, GripVertical, Eye,
  Search, Target, Handshake, Briefcase, Globe, Gift, 
  Home, TrendingUp, Users, Shield, Award
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import ImageUpload from '../../components/ImageUpload';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconOptions = [
  { value: 'search', label: 'Pesquisa', icon: Search },
  { value: 'target', label: 'Alvo', icon: Target },
  { value: 'handshake', label: 'Aperto de Mão', icon: Handshake },
  { value: 'briefcase', label: 'Maleta', icon: Briefcase },
  { value: 'globe', label: 'Globo', icon: Globe },
  { value: 'gift', label: 'Presente', icon: Gift },
  { value: 'home', label: 'Casa', icon: Home },
  { value: 'trending', label: 'Tendência', icon: TrendingUp },
  { value: 'users', label: 'Utilizadores', icon: Users },
  { value: 'shield', label: 'Escudo', icon: Shield },
  { value: 'award', label: 'Prémio', icon: Award },
];

const sectionTypes = [
  { value: 'hero', label: 'Hero (Cabeçalho)' },
  { value: 'text', label: 'Texto Simples' },
  { value: 'services', label: 'Lista de Serviços' },
  { value: 'values', label: 'Valores/Características' },
  { value: 'process', label: 'Processo/Passos' },
  { value: 'features', label: 'Funcionalidades' },
  { value: 'cta', label: 'Call to Action' },
  { value: 'contact_info', label: 'Informações de Contacto' },
];

const PageContentManagement = () => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [editedContent, setEditedContent] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [showAddSection, setShowAddSection] = useState(false);
  const [newSection, setNewSection] = useState({ type: 'text', title: '', title_en: '' });
  const [showPreview, setShowPreview] = useState(false);
  
  const getToken = () => localStorage.getItem('admin_token');

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/admin/login');
      return;
    }
    if (!loading && isAuthenticated) {
      loadPages();
    }
  }, [loading, isAuthenticated, navigate]);

  const loadPages = async () => {
    setLoadingData(true);
    try {
      const response = await axios.get(`${API_URL}/api/pages/admin/list`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setPages(response.data.pages || []);
      if (response.data.pages?.length > 0 && !selectedPage) {
        setSelectedPage(response.data.pages[0].page_id);
        setEditedContent(response.data.pages[0]);
      }
    } catch (error) {
      toast({ title: 'Erro', description: 'Erro ao carregar páginas', variant: 'destructive' });
    } finally {
      setLoadingData(false);
    }
  };

  const handlePageSelect = (pageId) => {
    const page = pages.find(p => p.page_id === pageId);
    setSelectedPage(pageId);
    setEditedContent(JSON.parse(JSON.stringify(page))); // Deep clone
    setExpandedSections({});
  };

  const handleSave = async () => {
    if (!editedContent) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/pages/admin/${selectedPage}`,
        { sections: editedContent.sections },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast({ title: 'Sucesso', description: 'Conteúdo guardado com sucesso' });
      loadPages();
    } catch (error) {
      toast({ title: 'Erro', description: 'Erro ao guardar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Tem certeza que deseja restaurar o conteúdo padrão? As alterações serão perdidas.')) return;
    
    try {
      await axios.post(
        `${API_URL}/api/pages/admin/${selectedPage}/reset`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast({ title: 'Sucesso', description: 'Conteúdo restaurado para o padrão' });
      // Reload pages to refresh UI with restored content
      await loadPages();
      loadPages();
    } catch (error) {
      toast({ title: 'Erro', description: 'Erro ao restaurar', variant: 'destructive' });
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const updateSection = (sectionIndex, field, value) => {
    setEditedContent(prev => {
      const newContent = { ...prev };
      newContent.sections = [...newContent.sections];
      newContent.sections[sectionIndex] = {
        ...newContent.sections[sectionIndex],
        [field]: value
      };
      return newContent;
    });
  };

  const updateSectionItem = (sectionIndex, itemIndex, field, value) => {
    setEditedContent(prev => {
      const newContent = { ...prev };
      newContent.sections = [...newContent.sections];
      newContent.sections[sectionIndex] = {
        ...newContent.sections[sectionIndex],
        items: [...(newContent.sections[sectionIndex].items || [])]
      };
      newContent.sections[sectionIndex].items[itemIndex] = {
        ...newContent.sections[sectionIndex].items[itemIndex],
        [field]: value
      };
      return newContent;
    });
  };

  const addItemToSection = (sectionIndex) => {
    setEditedContent(prev => {
      const newContent = { ...prev };
      newContent.sections = [...newContent.sections];
      const section = newContent.sections[sectionIndex];
      const items = section.items || [];
      
      let newItem = {};
      if (section.type === 'process') {
        newItem = { step: items.length + 1, title: '', title_en: '', description: '', description_en: '' };
      } else if (section.type === 'contact_info') {
        newItem = { type: 'phone', label: '', label_en: '', value: '' };
      } else {
        newItem = { icon: 'target', title: '', title_en: '', description: '', description_en: '' };
      }
      
      newContent.sections[sectionIndex] = {
        ...section,
        items: [...items, newItem]
      };
      return newContent;
    });
  };

  const removeItemFromSection = (sectionIndex, itemIndex) => {
    setEditedContent(prev => {
      const newContent = { ...prev };
      newContent.sections = [...newContent.sections];
      newContent.sections[sectionIndex] = {
        ...newContent.sections[sectionIndex],
        items: newContent.sections[sectionIndex].items.filter((_, i) => i !== itemIndex)
      };
      return newContent;
    });
  };

  const addSection = () => {
    const sectionId = `section-${Date.now()}`;
    const newSectionData = {
      id: sectionId,
      type: newSection.type,
      order: editedContent.sections.length,
      title: newSection.title,
      title_en: newSection.title_en,
      subtitle: '',
      subtitle_en: '',
      content: '',
      content_en: '',
      items: []
    };
    
    setEditedContent(prev => ({
      ...prev,
      sections: [...prev.sections, newSectionData]
    }));
    
    setShowAddSection(false);
    setNewSection({ type: 'text', title: '', title_en: '' });
    setExpandedSections(prev => ({ ...prev, [sectionId]: true }));
  };

  const removeSection = (sectionIndex) => {
    if (!window.confirm('Tem certeza que deseja remover esta secção?')) return;
    
    setEditedContent(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== sectionIndex)
    }));
  };

  const moveSection = (sectionIndex, direction) => {
    const newIndex = sectionIndex + direction;
    if (newIndex < 0 || newIndex >= editedContent.sections.length) return;
    
    setEditedContent(prev => {
      const newSections = [...prev.sections];
      [newSections[sectionIndex], newSections[newIndex]] = [newSections[newIndex], newSections[sectionIndex]];
      return { ...prev, sections: newSections };
    });
  };

  const getIconComponent = (iconName) => {
    const iconOption = iconOptions.find(o => o.value === iconName);
    return iconOption ? iconOption.icon : Target;
  };

  if (loading || loadingData) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="page-content-management">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestão de Conteúdo</h1>
            <p className="text-gray-500">Edite o conteúdo das páginas estáticas do site</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleReset} disabled={!selectedPage}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Restaurar Padrão
            </Button>
            <Button onClick={handleSave} disabled={saving || !editedContent}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Guardando...' : 'Guardar Alterações'}
            </Button>
          </div>
        </div>

        {/* Page Selector */}
        <div className="flex gap-2 flex-wrap">
          {pages.map(page => (
            <Button
              key={page.page_id}
              variant={selectedPage === page.page_id ? 'default' : 'outline'}
              onClick={() => handlePageSelect(page.page_id)}
              className="capitalize"
            >
              <FileText className="w-4 h-4 mr-2" />
              {page.page_name}
            </Button>
          ))}
        </div>

        {/* Content Editor */}
        {editedContent && (
          <div className="space-y-4">
            {/* Sections */}
            {editedContent.sections.map((section, sectionIndex) => (
              <Card key={section.id} className="border-l-4 border-l-blue-500">
                <CardHeader className="py-3 px-4 cursor-pointer" onClick={() => toggleSection(section.id)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <GripVertical className="w-5 h-5 text-gray-400" />
                      <div>
                        <CardTitle className="text-base">
                          {section.title || `Secção ${sectionIndex + 1}`}
                        </CardTitle>
                        <span className="text-xs text-gray-500 capitalize">{section.type}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); moveSection(sectionIndex, -1); }} disabled={sectionIndex === 0}>
                        <ChevronUp className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); moveSection(sectionIndex, 1); }} disabled={sectionIndex === editedContent.sections.length - 1}>
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); removeSection(sectionIndex); }} className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      {expandedSections[section.id] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </div>
                  </div>
                </CardHeader>
                
                {expandedSections[section.id] && (
                  <CardContent className="space-y-4 border-t">
                    {/* Common fields */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Título (PT)</label>
                        <Input
                          value={section.title || ''}
                          onChange={(e) => updateSection(sectionIndex, 'title', e.target.value)}
                          placeholder="Título em português"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Título (EN)</label>
                        <Input
                          value={section.title_en || ''}
                          onChange={(e) => updateSection(sectionIndex, 'title_en', e.target.value)}
                          placeholder="Title in English"
                        />
                      </div>
                    </div>

                    {/* Subtitle for hero, services, etc */}
                    {['hero', 'services', 'values', 'process', 'features'].includes(section.type) && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Subtítulo (PT)</label>
                          <Textarea
                            value={section.subtitle || ''}
                            onChange={(e) => updateSection(sectionIndex, 'subtitle', e.target.value)}
                            placeholder="Subtítulo em português"
                            rows={2}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Subtítulo (EN)</label>
                          <Textarea
                            value={section.subtitle_en || ''}
                            onChange={(e) => updateSection(sectionIndex, 'subtitle_en', e.target.value)}
                            placeholder="Subtitle in English"
                            rows={2}
                          />
                        </div>
                      </div>
                    )}

                    {/* Content for text sections */}
                    {section.type === 'text' && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Conteúdo (PT)</label>
                          <Textarea
                            value={section.content || ''}
                            onChange={(e) => updateSection(sectionIndex, 'content', e.target.value)}
                            placeholder="Texto em português"
                            rows={5}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Conteúdo (EN)</label>
                          <Textarea
                            value={section.content_en || ''}
                            onChange={(e) => updateSection(sectionIndex, 'content_en', e.target.value)}
                            placeholder="Text in English"
                            rows={5}
                          />
                        </div>
                      </div>
                    )}

                    {/* Items for services, values, process */}
                    {['services', 'values', 'process', 'features', 'contact_info'].includes(section.type) && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium">Itens</label>
                          <Button variant="outline" size="sm" onClick={() => addItemToSection(sectionIndex)}>
                            <Plus className="w-4 h-4 mr-1" />
                            Adicionar Item
                          </Button>
                        </div>
                        
                        {(section.items || []).map((item, itemIndex) => (
                          <div key={itemIndex} className="bg-gray-50 rounded-lg p-4 space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-600">
                                {section.type === 'process' ? `Passo ${item.step || itemIndex + 1}` : `Item ${itemIndex + 1}`}
                              </span>
                              <Button variant="ghost" size="sm" onClick={() => removeItemFromSection(sectionIndex, itemIndex)} className="text-red-500">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                            
                            {/* Icon selector for non-process types */}
                            {section.type !== 'process' && section.type !== 'contact_info' && (
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Ícone</label>
                                <Select
                                  value={item.icon || 'target'}
                                  onValueChange={(value) => updateSectionItem(sectionIndex, itemIndex, 'icon', value)}
                                >
                                  <SelectTrigger className="w-48">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {iconOptions.map(opt => {
                                      const IconComp = opt.icon;
                                      return (
                                        <SelectItem key={opt.value} value={opt.value}>
                                          <div className="flex items-center gap-2">
                                            <IconComp className="w-4 h-4" />
                                            {opt.label}
                                          </div>
                                        </SelectItem>
                                      );
                                    })}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}

                            {/* Contact info type */}
                            {section.type === 'contact_info' && (
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Tipo</label>
                                <Select
                                  value={item.type || 'phone'}
                                  onValueChange={(value) => updateSectionItem(sectionIndex, itemIndex, 'type', value)}
                                >
                                  <SelectTrigger className="w-48">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="phone">Telefone</SelectItem>
                                    <SelectItem value="email">Email</SelectItem>
                                    <SelectItem value="address">Morada</SelectItem>
                                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            )}

                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">
                                  {section.type === 'contact_info' ? 'Label (PT)' : 'Título (PT)'}
                                </label>
                                <Input
                                  value={item.title || item.label || ''}
                                  onChange={(e) => updateSectionItem(sectionIndex, itemIndex, section.type === 'contact_info' ? 'label' : 'title', e.target.value)}
                                  placeholder="Português"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">
                                  {section.type === 'contact_info' ? 'Label (EN)' : 'Título (EN)'}
                                </label>
                                <Input
                                  value={item.title_en || item.label_en || ''}
                                  onChange={(e) => updateSectionItem(sectionIndex, itemIndex, section.type === 'contact_info' ? 'label_en' : 'title_en', e.target.value)}
                                  placeholder="English"
                                />
                              </div>
                            </div>

                            {section.type === 'contact_info' ? (
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Valor</label>
                                <Input
                                  value={item.value || ''}
                                  onChange={(e) => updateSectionItem(sectionIndex, itemIndex, 'value', e.target.value)}
                                  placeholder="Ex: +351 XXX XXX XXX"
                                />
                              </div>
                            ) : (
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Descrição (PT)</label>
                                  <Textarea
                                    value={item.description || ''}
                                    onChange={(e) => updateSectionItem(sectionIndex, itemIndex, 'description', e.target.value)}
                                    placeholder="Descrição em português"
                                    rows={2}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Descrição (EN)</label>
                                  <Textarea
                                    value={item.description_en || ''}
                                    onChange={(e) => updateSectionItem(sectionIndex, itemIndex, 'description_en', e.target.value)}
                                    placeholder="Description in English"
                                    rows={2}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            ))}

            {/* Add Section Button */}
            <Card className="border-dashed">
              <CardContent className="py-6">
                <Button variant="outline" className="w-full" onClick={() => setShowAddSection(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Nova Secção
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Add Section Dialog */}
      <Dialog open={showAddSection} onOpenChange={setShowAddSection}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar Nova Secção</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Tipo de Secção</label>
              <Select value={newSection.type} onValueChange={(v) => setNewSection({...newSection, type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sectionTypes.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Título (PT)</label>
              <Input
                value={newSection.title}
                onChange={(e) => setNewSection({...newSection, title: e.target.value})}
                placeholder="Título da secção"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Título (EN)</label>
              <Input
                value={newSection.title_en}
                onChange={(e) => setNewSection({...newSection, title_en: e.target.value})}
                placeholder="Section title"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddSection(false)}>Cancelar</Button>
            <Button onClick={addSection}>Adicionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default PageContentManagement;
